const VALID_TYPES = ["booking", "container", "bl"];

export function normalizeTrackingType(raw, fallback = "container") {
  const tt = String(raw || fallback || "container").toLowerCase();
  return VALID_TYPES.includes(tt) ? tt : "container";
}

/** Regla API (nueva o legacy) → lista ordenada de fuentes. */
export function getTrackingSourcesFromRule(rule) {
  if (!rule || typeof rule !== "object") return [];
  if (Array.isArray(rule.trackingSources) && rule.trackingSources.length > 0) {
    return rule.trackingSources
      .filter((s) => s && String(s.trackingNumberField || "").trim())
      .map((s) => ({
        trackingNumberField: String(s.trackingNumberField).trim(),
        trackingType: normalizeTrackingType(s.trackingType, rule.defaultTrackingType),
      }));
  }
  const legacyField = String(rule.trackingNumberField || "").trim();
  if (!legacyField) return [];
  return [
    {
      trackingNumberField: legacyField,
      trackingType: normalizeTrackingType(rule.defaultTrackingType),
    },
  ];
}

export function findSourceForField(rule, fieldKey) {
  if (!fieldKey) return null;
  const key = String(fieldKey).toLowerCase();
  return (
    getTrackingSourcesFromRule(rule).find(
      (s) => String(s.trackingNumberField).toLowerCase() === key
    ) || null
  );
}

function getByPath(obj, path) {
  if (!obj || !path) return undefined;
  const parts = String(path).split(".");
  let cursor = obj;
  for (const part of parts) {
    if (cursor == null || typeof cursor !== "object") return undefined;
    cursor = cursor[part];
  }
  return cursor;
}

export function extractTrackingNumber(raw) {
  if (raw == null || raw === "") return "";
  if (Array.isArray(raw)) {
    const first = raw.find((v) => v != null && String(v).trim() !== "");
    return first != null ? String(first).trim() : "";
  }
  if (typeof raw === "object") {
    const s = raw.label || raw.name || raw.number || raw._id;
    return s != null ? String(s).trim() : "";
  }
  return String(raw).trim();
}

/** Sources with a non-empty tracking number, in configured order. */
export function collectTrackingCandidates(plain, rule) {
  const sources = getTrackingSourcesFromRule(rule);
  const candidates = [];
  for (const src of sources) {
    const trackingNumber = extractTrackingNumber(getByPath(plain, src.trackingNumberField));
    if (!trackingNumber) continue;
    candidates.push({
      trackingNumberField: src.trackingNumberField,
      trackingType: src.trackingType,
      trackingNumber,
    });
  }
  return candidates;
}

export function hasTrackingNumberToStart(plain, rule) {
  return collectTrackingCandidates(plain, rule).length > 0;
}

export function sanitizeTrackingSourcesForSave(rows) {
  if (!Array.isArray(rows)) return [];
  const out = [];
  const seen = new Set();
  for (const row of rows) {
    if (!row) continue;
    const trackingNumberField = String(row.trackingNumberField || "").trim();
    if (!trackingNumberField) continue;
    const lk = trackingNumberField.toLowerCase();
    if (seen.has(lk)) continue;
    seen.add(lk);
    out.push({
      trackingNumberField,
      trackingType: normalizeTrackingType(row.trackingType),
    });
  }
  return out;
}

/** Regla explícita en autoTrackingModels o legacy global (shipping + bookingField/containerField/blField). */
export function resolveAutoTrackingRuleFromConfig(config, modelName) {
  const mn = String(modelName || "").trim();
  if (!mn || mn.toLowerCase() === "tracking") return null;

  const list = Array.isArray(config?.autoTrackingModels) ? config.autoTrackingModels : [];
  const fromList = list.find(
    (r) => r && String(r.modelName || "").trim().toLowerCase() === mn.toLowerCase()
  );
  if (fromList) return fromList;

  if (mn.toLowerCase() !== "shipping") return null;

  const map = config?.trackingFieldMapping || {};
  const booking = String(map.booking || config?.bookingField || "").trim();
  const container = String(map.container || config?.containerField || "").trim();
  const bl = String(map.bl || config?.blField || "").trim();
  if (!booking && !container && !bl) return null;

  const trackingSources = [];
  if (container) {
    trackingSources.push({ trackingNumberField: container, trackingType: "container" });
  }
  if (booking) {
    trackingSources.push({ trackingNumberField: booking, trackingType: "booking" });
  }
  if (bl) {
    trackingSources.push({ trackingNumberField: bl, trackingType: "bl" });
  }
  if (trackingSources.length === 0) return null;

  const first = trackingSources[0];
  return {
    modelName: "shipping",
    trackingSources,
    trackingNumberField: first.trackingNumberField,
    defaultTrackingType: first.trackingType,
  };
}

/** Igual que el back: sin modo → 'auto'; `autoStartOnSave: false` (flag anterior) → 'manual'. */
export function getRuleOnSaveMode(rule) {
  const mode = String((rule && rule.onSaveMode) || "").trim().toLowerCase();
  if (["auto", "ask", "manual"].includes(mode)) return mode;
  if (rule && rule.autoStartOnSave === false) return "manual";
  return "auto";
}
